// The origin-country vocabulary the admin surfaces filter by: where a project (or
// an account) was CREATED from, as the browser-detected ISO 3166-1 alpha-2 code
// stamped at creation (backend/recruiter/model_tier.py, migration 0195).
//
// The set is OPEN (any code a browser reports), so unlike the device and model-tier
// filters the options come from the STORE (`GET /api/admin/countries`: the distinct
// codes with their project counts, most common first) rather than from a constant
// here. `unknown` is the filters' extra option and never a stored value: the column
// is NULL for a row older than 0195, an import, an MCP turn, or a browser that could
// not tell, and an operator asks for that bucket deliberately.
//
// Labels are rendered HERE with `Intl.DisplayNames`, so the wire and the URL carry
// codes and the reader sees names.

export const UNKNOWN_COUNTRY = "unknown";

const ISO_CODE = /^[A-Z]{2}$/;

let regionNames: Intl.DisplayNames | null | undefined;

/** The runtime's region-name table, resolved once. Null where the runtime has
 *  none (an old browser, a bare test runtime), in which case labels fall back
 *  to the code itself rather than throwing at the first row. */
function names(): Intl.DisplayNames | null {
  if (regionNames !== undefined) return regionNames;
  try {
    regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    regionNames = null;
  }
  return regionNames;
}

function nameOf(code: string): string | undefined {
  try {
    return names()?.of(code) ?? undefined;
  } catch {
    // `of` throws on a code that is syntactically a region but not a real one.
    return undefined;
  }
}

/** "India (IN)" for a code the runtime can name, the bare code when it cannot, and
 *  "Unknown" for the null bucket. Anything that is not a two-letter code prints as
 *  itself, so a value this bundle has never heard of is still visible. */
export function countryLabel(value: string | null | undefined): string {
  if (!value || value === UNKNOWN_COUNTRY) return "Unknown";
  const code = value.toUpperCase();
  if (!ISO_CODE.test(code)) return value;
  const name = nameOf(code);
  return name && name !== code ? `${name} (${code})` : code;
}

/** The `?countries=` URL param: a comma list of codes and/or `unknown`.
 *
 *  Codes are uppercased, the list deduped and sorted, so two spellings of one
 *  cohort are one URL, one query key and one compute on the server. Anything that
 *  is neither a two-letter code nor the sentinel drops out (a stale link's typo must
 *  not narrow the cohort to nothing while the control shows no selection). Absent
 *  when nothing survives, which is "every country". */
export function parseCountryParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const keys = value
    .split(",")
    .map((raw) => raw.trim())
    .map((raw) => (raw === UNKNOWN_COUNTRY ? raw : raw.toUpperCase()))
    .filter((raw) => raw === UNKNOWN_COUNTRY || ISO_CODE.test(raw));
  const unique = [...new Set(keys)].sort();
  return unique.length > 0 ? unique.join(",") : undefined;
}

/** The options a table's country column offers in SERVER mode: the store's
 *  vocabulary in the order the facet gives it (most common first) plus the null
 *  bucket LAST, as the absence rather than a peer of the real countries. Pure, so
 *  the Explorer, the worklists and the Searches list derive one list from one facet. */
export function countryFilterOptions(
  facets: readonly { code: string }[] | undefined,
): { value: string; label: string }[] {
  return [
    ...(facets ?? []).map((facet) => ({ value: facet.code, label: countryLabel(facet.code) })),
    { value: UNKNOWN_COUNTRY, label: countryLabel(UNKNOWN_COUNTRY) },
  ];
}
